import { FC, useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { FormikProvider, useFormik, FormikConfig } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { ClaimLinkOutput, Event } from "apiIntegrations/poap/Poap.interface";
import { claim } from "validators/client/poapEvent";

import { INFO_BOX_TYPE } from "components/InfoBox/types";
import InfoBox from "components/InfoBox";
import CustomButton from "components/CustomButton";
import CustomOutlinedInput from "components/CustomOutlinedInput";
import Recaptcha from "components/Recaptcha";
import ImageNext from "components/ImageNext";

import { STORAGE } from "enums/storage";

import { API_ROUTES } from "config/routes";

import { getObjectItem, setObjectItem } from "utils/storage";

import fetcher from "lib/fetchJson";

interface PoapClaim {
  address: string;
}

const initialState: PoapClaim = {
  address: "",
};

interface SectionPoapProps {
  poapEvent: Event;
  canClaim?: boolean;
}

type Status = "INITIAL" | "EMAIL" | "WALLET";

const SectionPoap: FC<SectionPoapProps> = ({ poapEvent, canClaim = true }) => {
  const [status, setStatus] = useState<Status>("INITIAL");
  const { executeRecaptcha } = useGoogleReCaptcha();
  const storageData = getObjectItem<PoapClaim>(STORAGE.POAP_FORM_DATA);

  const onSubmit: FormikConfig<PoapClaim>["onSubmit"] = useCallback(
    async (values) => {
      try {
        if (!executeRecaptcha) {
          return Promise.reject({
            message: "Execute recaptcha not yet available",
          });
        }
        const captchaToken = await executeRecaptcha("POAP_CLAIM");
        setObjectItem(STORAGE.POAP_FORM_DATA, values);
        const { data } = await fetcher<{ data: ClaimLinkOutput }>(
          API_ROUTES.POAP_CLAIM,
          { ...values, eventId: poapEvent.eventId, captchaToken },
          "POST",
        );
        setStatus(data.queue_uid ? "WALLET" : "EMAIL");
      } catch (e) {
        toast.error(e.data.message ?? "Something went wrong.");
      }
    },
    [executeRecaptcha, poapEvent],
  );

  const formik = useFormik({
    initialValues: storageData ?? initialState,
    validationSchema: toFormikValidationSchema(claim),
    validateOnChange: false,
    onSubmit: onSubmit,
  });

  const { linksCount = 0, claimedLinksCount = 0 } = poapEvent;

  const allLinksMinted = linksCount - claimedLinksCount === 0;

  if (status === "INITIAL") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 5,
          alignItems: "center",
        }}
      >
        <ImageNext
          src={`${poapEvent.image_url}?size=small`}
          width={140}
          height={140}
          alt={`${poapEvent.name} poap logo`}
          style={{ borderRadius: "50%" }}
          boxProps={{
            sx: {
              maxWidth: 140,
            },
          }}
        />
        <Box width="100%">
          <Typography paragraph>Mint your “{poapEvent.name}” POAP</Typography>
          <Typography paragraph mb={3}>
            Each POAP is a gift from an issuer to collectors, in celebration of
            a special shared memory.
          </Typography>
          <FormikProvider value={formik}>
            <Box component="form" onSubmit={formik.handleSubmit}>
              {allLinksMinted ? (
                <InfoBox type={INFO_BOX_TYPE.INFO}>
                  All the available POAPs have already been minted
                </InfoBox>
              ) : (
                <Typography paragraph mb={0}>
                  <Box component="span" sx={{ fontWeight: 400 }}>
                    Available POAPs:{" "}
                  </Box>
                  {linksCount - claimedLinksCount}/{linksCount}
                </Typography>
              )}

              <CustomOutlinedInput
                label="Type in your e-mail address, wallet address or ENS to mint POAP"
                labelProps={{
                  fontWeight: 600,
                }}
                inputProps={{
                  id: "address",
                  placeholder: "Address",
                  value: formik.values.address,
                  onChange: formik.handleChange,
                  disabled: allLinksMinted,
                }}
                formControlProps={{
                  fullWidth: true,
                  margin: "normal",
                }}
                error={formik.errors.address}
              />

              <Recaptcha boxProps={{ mb: 2 }} />

              <CustomButton
                sx={{
                  textTransform: "uppercase",
                  ml: "auto",
                  display: "block",
                }}
                type="submit"
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting || allLinksMinted || !canClaim}
                color="warning"
              >
                Collect
              </CustomButton>
            </Box>
          </FormikProvider>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <InfoBox type={INFO_BOX_TYPE.SUCCESS}>
        {status === "EMAIL"
          ? "Congratulations! POAP (Proof of Attendance Protocol) has been reserved for you. Wait for an e-mail and finish the minting process."
          : ""}

        {status === "WALLET"
          ? "Congratulations! POAP (Proof of Attendance Protocol) has been minted on your account. Go to the POAP app and check it out."
          : ""}
      </InfoBox>
    </Box>
  );
};

export default SectionPoap;
